import { useEffect, useState } from 'react'
import { adminApi } from '../../services/api'
import { useToast } from '../../contexts/ToastContext'

type ReorderModalProps = {
  isOpen: boolean
  orderId: string | null
  onClose: () => void
  onSuccess?: () => void
}

const PICKUP_SLOTS = ['9AM-11AM', '11AM-1PM', '2PM-4PM', '4PM-6PM']

const getTomorrow = () => {
  const next = new Date()
  next.setDate(next.getDate() + 1)
  return next.toISOString().slice(0, 10)
}

export default function ReorderModal({ isOpen, orderId, onClose, onSuccess }: ReorderModalProps) {
  const [pickupDate, setPickupDate] = useState(getTomorrow())
  const [pickupSlot, setPickupSlot] = useState(PICKUP_SLOTS[0])
  const [paymentMode, setPaymentMode] = useState<'cod' | 'online'>('cod')
  const [adminNotes, setAdminNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const { addToast } = useToast()

  useEffect(() => {
    if (!isOpen) {
      return
    }
    setPickupDate(getTomorrow())
    setPickupSlot(PICKUP_SLOTS[0])
    setPaymentMode('cod')
    setAdminNotes('')
    setSaving(false)
    setError('')
  }, [isOpen])

  if (!isOpen || !orderId) {
    return null
  }

  const handleSubmit = async () => {
    if (!pickupDate || !pickupSlot) {
      setError('Pickup date and slot are required.')
      return
    }

    setSaving(true)
    setError('')

    try {
      await adminApi.reorderFromPrevious(orderId, {
        pickup_date: pickupDate,
        pickup_slot: pickupSlot,
        payment_mode: paymentMode,
        admin_notes: adminNotes.trim() || undefined,
      })

      addToast('Reorder created successfully', 'success')
      onClose()
      if (onSuccess) {
        onSuccess()
      }
    } catch (submitError) {
      setError('Unable to create reorder. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
      onClick={onClose}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: '420px',
          background: '#FFFFFF',
          borderRadius: '18px',
          padding: '24px',
          boxShadow: '0px 24px 64px rgba(15,46,42,0.20)',
        }}
      >
        <h3 className="font-display" style={{ margin: '0 0 6px', fontSize: '20px', color: '#0F2E2A' }}>
          Reorder
        </h3>
        <p className="font-body" style={{ margin: '0 0 16px', color: '#9CAB9A', fontSize: '12px' }}>
          Confirm pickup details and payment mode.
        </p>

        {error ? (
          <div className="font-body" style={{ marginBottom: '12px', background: '#FEF2F2', color: '#991B1B', borderRadius: '10px', padding: '10px 12px', fontSize: '12px', fontWeight: 600 }}>
            {error}
          </div>
        ) : null}

        <div style={{ display: 'grid', gap: '12px' }}>
          <div>
            <label className="font-body" style={{ display: 'block', fontSize: '12px', color: '#9CAB9A', marginBottom: '6px' }}>
              Pickup Date
            </label>
            <input
              type="date"
              value={pickupDate}
              onChange={(event) => setPickupDate(event.target.value)}
              className="font-body"
              style={{ width: '100%', height: '42px', border: '1.5px solid #EAE4D8', borderRadius: '10px', padding: '0 12px' }}
            />
          </div>

          <div>
            <label className="font-body" style={{ display: 'block', fontSize: '12px', color: '#9CAB9A', marginBottom: '6px' }}>
              Pickup Slot
            </label>
            <select
              value={pickupSlot}
              onChange={(event) => setPickupSlot(event.target.value)}
              className="font-body"
              style={{ width: '100%', height: '42px', border: '1.5px solid #EAE4D8', borderRadius: '10px', padding: '0 12px' }}
            >
              {PICKUP_SLOTS.map((slot) => (
                <option key={slot} value={slot}>
                  {slot}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="font-body" style={{ display: 'block', fontSize: '12px', color: '#9CAB9A', marginBottom: '6px' }}>
              Payment Mode
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {(['cod', 'online'] as const).map((mode) => {
                const active = paymentMode === mode
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setPaymentMode(mode)}
                    className="font-body"
                    style={{
                      flex: 1,
                      height: '38px',
                      borderRadius: '999px',
                      border: `1.5px solid ${active ? '#D6B97B' : '#EAE4D8'}`,
                      background: active ? '#FFFBF5' : '#FFFFFF',
                      color: active ? '#0F2E2A' : '#4A5568',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {mode.toUpperCase()}
                  </button>
                )
              })}
            </div>
          </div>

          <div>
            <label className="font-body" style={{ display: 'block', fontSize: '12px', color: '#9CAB9A', marginBottom: '6px' }}>
              Admin Notes (optional)
            </label>
            <textarea
              value={adminNotes}
              onChange={(event) => setAdminNotes(event.target.value)}
              className="font-body"
              style={{ width: '100%', minHeight: '80px', border: '1.5px solid #EAE4D8', borderRadius: '10px', padding: '10px 12px', resize: 'vertical' }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
          <button
            type="button"
            className="font-body"
            onClick={onClose}
            style={{ flex: 1, height: '42px', border: '1.5px solid #EAE4D8', borderRadius: '10px', background: 'transparent', fontWeight: 600, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="font-body"
            onClick={() => void handleSubmit()}
            disabled={saving}
            style={{ flex: 1, height: '42px', border: 'none', borderRadius: '10px', background: '#0F2E2A', color: '#FFFFFF', fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Creating...' : 'Create Reorder'}
          </button>
        </div>
      </div>
    </div>
  )
}
